/**
 * Query Key Factory
 *
 * Hierarchical query keys for React Query. Enables targeted invalidation:
 * - Invalidate all intl-orders data: ordersKeys.all
 * - Invalidate the orders list: ordersKeys.list(params)
 * - Invalidate one order: ordersKeys.detail(orderId)
 * - Invalidate stock for a location: ordersKeys.stock(locationId)
 */

export const ordersKeys = {
  all: ["intl-orders"] as const,
  list: (params: Record<string, unknown>) => [...ordersKeys.all, "list", params] as const,
  detail: (orderId: number) => [...ordersKeys.all, "detail", orderId] as const,
  stock: (locationId?: number) => [...ordersKeys.all, "stock", locationId] as const,
};

export const ordersQueryDefaults = (options?: {
  staleTime?: number;
  gcTime?: number;
}): { staleTime: number; gcTime: number } => ({
  staleTime: options?.staleTime ?? 2 * 60 * 1000,
  gcTime: options?.gcTime ?? 10 * 60 * 1000,
});

/**
 * Admin query keys (intl_orders:admin — /intl-orders/admin/*).
 * - Invalidate all admin data: adminKeys.all
 * - Invalidate the effective config: adminKeys.config()
 * - Invalidate one location's catalog: adminKeys.catalog(locationId)
 * - Invalidate every catalog list regardless of location: adminKeys.catalogRoot()
 * - Invalidate the partners list: adminKeys.partners()
 */
export const adminKeys = {
  all: ["intl-admin"] as const,
  config: () => [...adminKeys.all, "config"] as const,
  catalogRoot: () => [...adminKeys.all, "catalog"] as const,
  catalog: (locationId?: number) => [...adminKeys.catalogRoot(), locationId] as const,
  partners: () => [...adminKeys.all, "partners"] as const,
};
